/**
 * V2 NavList – renders sub-nav in a portal so it appears as side popup (not affected by nav overflow).
 */
import React, { useRef, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { hasProperty, isEmpty } from "../../../utils/common";
import { Text } from "../../../components/styledConstants";
import { SubNavPopup, NavIconV2 } from "./NavigationV2.style";
import { useSelector } from "react-redux";

/** True if this sub-nav entry's route is the current page (or a nested page under it). */
function subItemMatchesCurrentPath(sub, path) {
  const link = sub.link || "";
  if (!link) return false;
  if (path === link) return true;
  const normalized = link.endsWith("/") ? link.slice(0, -1) : link;
  if (path.startsWith(`${normalized}/`)) return true;
  if (link === "/payback/approval-user-list" && path.startsWith("/payback/edit-approval-user")) return true;
  if (link === "/payback" && path === "/add/payback") return true;
  return false;
}

export default function NavListV2({ navData = [], toggleNavigation = () => {}, hideSubmenu, className = "" }) {
  useSelector((state) => state.user);
  const location = useLocation();
  const isSubNavList = className.includes("sub-navlist");
  const triggerRef = useRef(null);
  const [subNavRect, setSubNavRect] = useState(null);
  const expandedIndex = navData.findIndex((i) => i.expanded);

  const SUBNAV_GAP = 8; // Gap between sidebar and sub-nav popup
  const POPUP_EST_HEIGHT = 140; // Approx height for sub-nav with 2–3 items
  const VIEWPORT_PADDING = 24; // Min space from viewport edges

  useLayoutEffect(() => {
    if (expandedIndex >= 0 && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const left = rect.right + SUBNAV_GAP;
      const openAbove = rect.bottom + POPUP_EST_HEIGHT > window.innerHeight - VIEWPORT_PADDING;
      const availableBelow = Math.max(160, window.innerHeight - rect.top - VIEWPORT_PADDING);
      const availableAbove = Math.max(160, rect.bottom - VIEWPORT_PADDING);
      const popupMaxHeight = Math.min(560, openAbove ? availableAbove : availableBelow);
      setSubNavRect({
        left,
        maxHeight: popupMaxHeight,
        ...(openAbove
          ? { bottom: window.innerHeight - rect.bottom }
          : { top: rect.top }),
      });
    } else {
      setSubNavRect(null);
    }
  }, [expandedIndex]);

  return (
    <>
      <ul className={className}>
        {navData.length >= 0 &&
          navData.map((item, i) => {
              const sectionActive =
                !isSubNavList &&
                !isEmpty(item.subNav) &&
                item.subNav.some((sub) => subItemMatchesCurrentPath(sub, location.pathname));
              const parentHighlighted = item.expanded || sectionActive;
              return (
            <li className={parentHighlighted ? "active" : ""} key={`nav-${item.link || item.title || i}`}>
              {!isEmpty(item.subNav) ? (
                <>
                  <div
                    ref={item.expanded ? triggerRef : null}
                    className="nav-link nav-box flex flex-center"
                    onClick={hasProperty(item, "subNav") ? (e) => toggleNavigation(e, item.expanded, i) : () => {}}
                    role="list"
                  >
                    {item.icon && <NavIconV2>{item.icon}</NavIconV2>}
                    <Text size="rg" color={parentHighlighted ? "color6" : "color15"}>
                      {item.title}
                    </Text>
                  </div>
                  {hasProperty(item, "expanded") &&
                    item.expanded &&
                    subNavRect &&
                    createPortal(
                      <SubNavPopup
                        data-v2-subnav-popup
                        style={{
                          left: subNavRect.left,
                          maxHeight: subNavRect.maxHeight,
                          ...(subNavRect.top !== undefined
                            ? { top: subNavRect.top }
                            : { bottom: subNavRect.bottom }),
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <NavListV2 navData={item.subNav} className="sub-navlist" hideSubmenu={hideSubmenu} />
                      </SubNavPopup>,
                      document.body
                    )}
                </>
              ) : (
                <NavLink
                  to={item.link}
                  end={isSubNavList}
                  onClick={hideSubmenu}
                  className={({ isActive }) => {
                    const editApprovalActive =
                      isSubNavList &&
                      item.link === "/payback/approval-user-list" &&
                      location.pathname.startsWith("/payback/edit-approval-user");
                    const addPaybackActive =
                      isSubNavList && item.link === "/payback" && location.pathname === "/add/payback";
                    const active = isActive || editApprovalActive || addPaybackActive;
                    return ["nav-link", "flex", "flex-center", active ? "active" : ""].filter(Boolean).join(" ");
                  }}
                >
                  {item.icon && <NavIconV2>{item.icon}</NavIconV2>}
                  <Text size="rg" color="color15" className="label">
                    {item.title}
                  </Text>
                </NavLink>
              )}
            </li>
              );
            })}
      </ul>
    </>
  );
}
